import { forwardRef } from 'react'
import { cn } from '../utils/cn.ts'
import { Card, CardTitle, CardSubtitle, CardContent, CardAction } from '../components/Card.tsx'
import { Button } from '../components/Button.tsx'

export interface RSVPCardProps {
  status: 'unanswered' | 'confirmed' | 'cant-go'
  title: string
  subtitle?: string
  statusLabel?: string
  error?: string | null
  disabled?: boolean
  onStatusChange: (newStatus: 'confirmed' | 'cant-go') => void
  className?: string
}

export const RSVPCard = forwardRef<HTMLDivElement, RSVPCardProps>(
  ({ status, title, subtitle, statusLabel, error, disabled, onStatusChange, className }, ref) => {
    const isConfirmed = status === 'confirmed'
    const isUnanswered = status === 'unanswered'

    return (
      <Card ref={ref} className={cn(className)}>
        <CardTitle>{title}</CardTitle>
        {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        {!isUnanswered && statusLabel && (
          <CardContent>
            <div className="status-row">
              <span
                className={cn(
                  'status-row-icon',
                  isConfirmed ? 'status-row-icon-confirmed' : 'status-row-icon-declined',
                )}
              >
                {isConfirmed ? '\u2713' : '\u2715'}
              </span>
              <span
                className={cn(
                  'status-row-label',
                  isConfirmed ? 'status-row-label-confirmed' : 'status-row-label-declined',
                )}
              >
                {statusLabel}
              </span>
            </div>
          </CardContent>
        )}
        {error && (
          <CardContent>
            <p className="rsvp-card-error" role="alert">{error}</p>
          </CardContent>
        )}
        <CardAction>
          <div className="rsvp-card-actions">
            <Button
              variant="primary"
              disabled={disabled || isConfirmed}
              aria-current={isConfirmed ? 'true' : undefined}
              onClick={() => onStatusChange('confirmed')}
            >
              Going
            </Button>
            <Button
              variant="secondary"
              disabled={disabled || status === 'cant-go'}
              aria-current={status === 'cant-go' ? 'true' : undefined}
              onClick={() => onStatusChange('cant-go')}
            >
              Can&apos;t Go
            </Button>
          </div>
        </CardAction>
      </Card>
    )
  },
)

RSVPCard.displayName = 'RSVPCard'
